// components/InternalLinkParser.tsx
import { useCurrentPath } from "@/hooks";
import { useDataStore, useFilterStore } from "@/stores";
import { Dictionary } from "@/utils/Dictionary";
import { InternalLinkUtils } from "@/utils/InternalLinkUtils";
import React, { ElementType, useCallback, useMemo } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";

type Page = { id: string; url: string };
type ElementPage = { slug: string };

// Polymorphic props (no ref)
type InternalLinkParserProps<T extends ElementType> = {
    as?: T;
    html: string;
    className?: string;
} & Omit<React.ComponentPropsWithoutRef<T>, "children" | "dangerouslySetInnerHTML" | "className">;

export default function InternalLinkParser<T extends ElementType = "div">(props: InternalLinkParserProps<T>) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { as, html, className, onClickCapture: userOnClickCapture, ...rest } = props as InternalLinkParserProps<any>;

    const Tag: ElementType = as ?? "div";

    const pages = useDataStore((s) => s.pages) as Page[];
    const elements = useDataStore((s) => s.elements) as Dictionary<ElementPage[]>;
    const activeVersion = useFilterStore((s) => s.activeVersion);

    const params = useParams();
    const currentPath = useCurrentPath();
    const navigate = useNavigate();

    const pageHrefById = useMemo(() => {
        const map = new Map<string, string>();
        for (const page of pages) {
            map.set(page.id, generatePath(currentPath, { ...params, "*": page.url }).replace(":/", ""));
        }

        return map;
    }, [pages, currentPath, params]);

    const elementHrefBySlug = useMemo(() => {
        const map = new Map<string, string>();
        for (const element of elements[activeVersion!] ?? []) {
            map.set(
                element.slug,
                generatePath(currentPath, { ...params, "*": `elements/${element.slug}` }).replace(":/", "")
            );
        }

        return map;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elements, currentPath, params]);

    const resolvedHtml = useMemo(
        () =>
            InternalLinkUtils.resolveInternalLinksInHtml(html ?? "", {
                pageHrefById,
                elementHrefBySlug,
            }),
        [html, pageHrefById, elementHrefBySlug]
    );

    const handleClickCapture = useCallback(
        (e: React.MouseEvent) => {
            const a = (e.target as HTMLElement).closest("a") as HTMLAnchorElement | null;
            if (a?.dataset.spaLink === "1") {
                const href = a.getAttribute("href");
                if (href) {
                    e.preventDefault();
                    navigate(href);
                }
            }
            // compose user handler (if provided)
            if (typeof userOnClickCapture === "function") {
                userOnClickCapture(e);
            }
        },
        [navigate, userOnClickCapture]
    );

    return (
        <Tag
            className={className}
            onClickCapture={handleClickCapture}
            dangerouslySetInnerHTML={{ __html: resolvedHtml }}
            {...rest}
        />
    );
}
